import { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Spin, message } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { vppSettlement } from '../../../services/api';

export default function VppSettlement() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    Promise.all([vppSettlement.overview(), vppSettlement.list()])
      .then(([ovRes, listRes]) => {
        setOverview(ovRes.data);
        setRecords((listRes.data || []).map(r => ({
          key: r.id,
          period: r.settlement_period,
          revenue: parseFloat(r.total_revenue),
          cost: parseFloat(r.total_cost || r.penalty_amount || 0),
          profit: parseFloat(r.net_profit || r.total_revenue - (r.total_cost || 0)),
          // 分成字段缺失时显示 "—"，不按固定比例编造
          platform: r.platform_income != null ? parseFloat(r.platform_income) : null,
          resource: r.resource_income != null ? parseFloat(r.resource_income) : null,
          status: r.status || (r.is_confirmed ? '已结算' : '待结算'),
        })));
      })
      .catch(err => message.error(err.message || '加载结算数据失败'))
      .finally(() => setLoading(false));
  }, []);

  const revenueOption = overview ? {
    tooltip: { trigger: 'axis' },
    legend: { data: ['收入', '成本', '净利润'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: overview.monthly_breakdown.map(m => m.month.slice(5) + '月') },
    yAxis: { type: 'value', name: '元' },
    series: [
      { name: '收入', type: 'bar', data: overview.monthly_breakdown.map(m => m.revenue), itemStyle: { color: '#1890ff' } },
      { name: '成本', type: 'bar', data: overview.monthly_breakdown.map(m => m.cost), itemStyle: { color: '#ff4d4f' } },
      { name: '净利润', type: 'line', data: overview.monthly_breakdown.map(m => m.profit), itemStyle: { color: '#52c41a' } },
    ]
  } : {};

  const pieOption = overview ? {
    tooltip: { trigger: 'item', formatter: '{b}: {c} 元 ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      data: overview.revenue_by_source.map(s => ({
        name: s.source,
        value: s.amount,
        itemStyle: { color: s.source.includes('电力') ? '#1890ff' : s.source.includes('辅助') ? '#faad14' : '#52c41a' },
      })),
      label: { formatter: '{b}\n{d}%' }
    }]
  } : {};

  const handleExport = async (period) => {
    try {
      await vppSettlement.export(period ? { period } : {});
      message.success('结算明细已导出');
    } catch (err) {
      message.error(err.message || '导出失败');
    }
  };

  const columns = [
    { title: '结算周期', dataIndex: 'period' },
    { title: '总收入(元)', dataIndex: 'revenue', render: v => v.toLocaleString() },
    { title: '总成本(元)', dataIndex: 'cost', render: v => v.toLocaleString() },
    { title: '净利润(元)', dataIndex: 'profit', render: v => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{v.toLocaleString()}</span> },
    { title: '平台分成', dataIndex: 'platform', render: v => v == null ? '—' : v.toLocaleString() },
    { title: '资源方分成', dataIndex: 'resource', render: v => v == null ? '—' : v.toLocaleString() },
    { title: '状态', dataIndex: 'status', render: v => <Tag color={v === '已结算' ? 'green' : 'default'}>{v}</Tag> },
    { title: '操作', render: (_, record) => <Button size="small" onClick={() => handleExport(record.period)}>导出</Button> },
  ];

  const profitRate = overview && overview.total_revenue > 0
    ? ((overview.net_profit / overview.total_revenue) * 100).toFixed(1)
    : 0;

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!overview) return <div>暂无数据</div>;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="累计收入" value={overview.total_revenue} suffix="元" valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="累计成本" value={overview.total_cost} suffix="元" valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="累计净利润" value={overview.net_profit} suffix="元" valueStyle={{ color: '#52c41a', fontSize: 20 }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="综合收益率" value={profitRate} suffix="%" precision={1} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={16}><Card title="月度收益趋势" size="small"><ReactEChartsCore option={revenueOption} style={{ height: 280 }} /></Card></Col>
        <Col span={8}><Card title="收益来源" size="small"><ReactEChartsCore option={pieOption} style={{ height: 280 }} /></Card></Col>
      </Row>
      <Card title="结算记录" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={records} pagination={false} size="small" />
      </Card>
    </div>
  );
}
